import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Box, Skeleton, Typography } from "@mui/material";
import HotelIcon from "@mui/icons-material/Hotel";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdCardSx,
  ipdPageSx,
  ipdSemanticSx,
  IpdButton,
  IpdPageHeader,
  IpdSectionCard,
} from "../../../ipd/utils";
import { EmergencyPlaceholder } from "../../components/EmergencyBadges";
import { formatDateTime } from "../../constants";
import { getBedOccupancy, markBedClean } from "../../api/emergencyApi";

/**
 * Bed Management occupancy (ER-BED-*, ER-SETUP-004).
 *
 * The state of every emergency bed in one place — free, occupied or being
 * cleaned — so capacity decisions are made without walking the department. An
 * emergency bed has exactly those three states and no hold or reservation,
 * unlike an inpatient bed; the screen says so rather than leaving it implied.
 *
 * The only change made here is marking a cleaned bed ready. Placing and
 * releasing occupied beds happens on the visit, because an occupied bed's
 * status is a consequence of the patient in it.
 */

const STATUS_META = {
  free: { label: "Free", variant: "success" },
  occupied: { label: "Occupied", variant: "info" },
  cleaning: { label: "Being cleaned", variant: "warning" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, variant: "info" };
  return (
    <Box
      component="span"
      sx={{
        ...ipdSemanticSx(m.variant),
        px: 1,
        py: 0.3,
        borderRadius: `${IPD_SHAPE.radiusSm}px`,
        fontSize: 11.5,
        lineHeight: 1.6,
        whiteSpace: "nowrap",
      }}
    >
      {m.label}
    </Box>
  );
}

/** One count in the summary strip. */
function TotalTile({ label, value, variant }) {
  // Semantic variants (success/warning/info) resolve to a {bg,text,border}
  // token; "primary" is a plain hex string. Accept both so every tile gets its
  // accent colour rather than silently falling back to grey.
  const token = IPD_COLORS[variant];
  const accent =
    token && typeof token === "object"
      ? token.text
      : typeof token === "string"
      ? token
      : IPD_COLORS.border;
  return (
    <Box
      sx={{
        ...ipdCardSx,
        flex: "1 1 120px",
        minWidth: 120,
        p: 1.75,
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: IPD_COLORS.textPrimary, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>{label}</Typography>
    </Box>
  );
}

function BedTile({ bed, onOpenVisit, onClean, cleaning }) {
  const occupant = bed.occupant;
  return (
    <Box
      sx={{
        ...ipdCardSx,
        p: 1.75,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minHeight: 96,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: IPD_COLORS.textPrimary }}>
          {bed.label}
        </Typography>
        <StatusBadge status={bed.status} />
      </Box>

      {bed.status === "occupied" ? (
        occupant ? (
          <Box
            role="link"
            tabIndex={0}
            aria-label={`Open ${occupant.display_name}'s visit`}
            onClick={() => onOpenVisit(occupant.visit_id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpenVisit(occupant.visit_id);
              }
            }}
            sx={{
              cursor: "pointer",
              borderRadius: `${IPD_SHAPE.radiusSm}px`,
              "&:hover .occ-name": { textDecoration: "underline" },
              "&:focus-visible": { outline: `2px solid ${IPD_COLORS.primary}`, outlineOffset: 2 },
            }}
          >
            <Typography
              className="occ-name"
              sx={{ fontSize: 13, fontWeight: 600, color: IPD_COLORS.textPrimary }}
            >
              {occupant.display_name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: occupant.provider_name ? IPD_COLORS.textSecondary : IPD_COLORS.textMuted }}>
              {occupant.provider_name || "No provider named"}
            </Typography>
            {occupant.since && (
              <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                Since {formatDateTime(occupant.since)}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>
            Occupied — the visit could not be resolved
          </Typography>
        )
      ) : bed.status === "cleaning" ? (
        <Box sx={{ mt: "auto" }}>
          <IpdButton
            variant="outlined"
            size="small"
            startIcon={<CleaningServicesIcon />}
            loading={cleaning}
            onClick={() => onClean(bed)}
          >
            Mark clean
          </IpdButton>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted, mt: "auto" }}>
          Ready for the next patient
        </Typography>
      )}
    </Box>
  );
}

export default function BedManagement() {
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");
  const [cleaningId, setCleaningId] = useState(null);

  // Only the newest fetch may write state — a Refresh mid-flight must not be
  // overwritten by a slower earlier response.
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const res = await getBedOccupancy();
      if (reqIdRef.current !== reqId) return;
      setData(res);
      setLoadError("");
    } catch (e) {
      if (reqIdRef.current !== reqId) return;
      setData(null);
      setLoadError(e?.message || "Could not load bed occupancy");
    } finally {
      if (reqIdRef.current === reqId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const clean = async (bed) => {
    setCleaningId(bed.id);
    setActionError("");
    setNotice("");
    try {
      await markBedClean(bed.id);
      setNotice(`${bed.label} is clean and free.`);
      await load();
    } catch (e) {
      setActionError(e?.message || "Could not mark the bed clean");
    } finally {
      setCleaningId(null);
    }
  };

  const openVisit = (visitId) => history.push(`/emergency_visit/${visitId}`);

  const totals = data?.totals || { free: 0, occupied: 0, cleaning: 0, total: 0 };
  const categories = data?.categories || [];
  const beds = data?.beds || [];
  const bedsByCategory = (categoryId) => beds.filter((b) => b.category_id === categoryId);

  return (
    <Box sx={{ ...ipdPageSx, p: { xs: 2, md: 3 } }}>
      <IpdPageHeader
        eyebrow="Emergency Department"
        title="Bed Management"
        description="Free, occupied and being-cleaned — the whole department at a glance."
        actions={
          <>
            <IpdButton
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push("/emergency_tracking_board")}
            >
              Board
            </IpdButton>
            <IpdButton variant="outlined" startIcon={<RefreshIcon />} onClick={load}>
              Refresh
            </IpdButton>
          </>
        }
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }} onClose={() => setActionError("")}>
          {actionError}
        </Alert>
      )}

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          action={
            <IpdButton variant="outlined" size="small" onClick={load}>
              Retry
            </IpdButton>
          }
        >
          {loadError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={78} sx={{ flex: "1 1 120px", borderRadius: `${IPD_SHAPE.radiusMd}px` }} />
            ))}
          </Box>
          <Skeleton variant="rounded" height={220} sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }} />
        </Box>
      ) : !data ? null : beds.length === 0 ? (
        <EmergencyPlaceholder
          icon={HotelIcon}
          title="No emergency beds yet"
          description="Add bed categories and beds in Department Setup — they appear here as soon as they exist."
          action={
            <IpdButton onClick={() => history.push("/emergency_department_setup")}>
              Department Setup
            </IpdButton>
          }
        />
      ) : (
        <>
          {/* Summary strip */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
            <TotalTile label="Free" value={totals.free} variant="success" />
            <TotalTile label="Occupied" value={totals.occupied} variant="info" />
            <TotalTile label="Being cleaned" value={totals.cleaning} variant="warning" />
            <TotalTile label="Total beds" value={totals.total} variant="primary" />
          </Box>
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textMuted, mb: 2.5 }}>
            An emergency bed is only ever free, occupied or being cleaned — there is no hold or reservation.
          </Typography>

          {/* One section per category, so counts read against the beds they cover. */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {categories.map((cat) => {
              const rows = bedsByCategory(cat.id);
              if (rows.length === 0) return null;
              const c = cat.counts || {};
              return (
                <IpdSectionCard
                  key={cat.id}
                  title={cat.active ? cat.name : `${cat.name} (retired)`}
                  subtitle={`${c.free || 0} free · ${c.occupied || 0} occupied · ${c.cleaning || 0} being cleaned`}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                        lg: "repeat(4, 1fr)",
                      },
                      gap: 1.5,
                    }}
                  >
                    {rows.map((bed) => (
                      <BedTile
                        key={bed.id}
                        bed={bed}
                        onOpenVisit={openVisit}
                        onClean={clean}
                        cleaning={cleaningId === bed.id}
                      />
                    ))}
                  </Box>
                </IpdSectionCard>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}
